/**
 * Pre-Contact Modal — Sheet-driven multi-step contact form.
 *
 * Triggered by any element with [data-modal-trigger] (typically a mailto:
 * anchor inside the Farm Contact Page or Farm Contact CTA section).
 *
 * Pulls questions, smart-resolution rules, and copy from a Google Apps
 * Script Web App. The Apps Script URL is set per-section in the Theme
 * Editor and read off the <dialog data-pre-contact-modal data-endpoint>.
 *
 * If the modal isn't mounted on a page, triggers fall through to the real
 * mailto: href — JS-off and pages without the modal still work.
 */
(function () {
  'use strict';

  const UPLOADCARE_PUBLIC_KEY = '725538c753a9d9ec0c41';
  const UPLOADCARE_ENDPOINT = 'https://upload.uploadcare.com/base/';
  const UPLOADCARE_CDN = 'https://ucarecdn.com/';
  const SUPPORTED_INPUT_TYPES = ['radio', 'select', 'text', 'email', 'tel', 'number', 'textarea', 'photo', 'contact', 'info'];

  const dialog = document.querySelector('[data-pre-contact-modal]');
  if (!dialog) return; // Page doesn't render the modal — let mailto: do its thing.

  const endpoint = dialog.dataset.endpoint;
  if (!endpoint) {
    console.warn('[pre-contact-modal] No endpoint set; modal disabled.');
    return;
  }

  const $body = dialog.querySelector('[data-modal-body]');
  const $loading = dialog.querySelector('[data-modal-loading]');
  const $stepHost = dialog.querySelector('[data-step-host]');
  const $confirmHost = dialog.querySelector('[data-confirmation-host]');
  const $error = dialog.querySelector('[data-modal-error]');
  const $title = dialog.querySelector('[data-modal-title]');
  const $footer = dialog.querySelector('[data-modal-footer]');
  const $next = dialog.querySelector('[data-modal-next]');
  const $back = dialog.querySelector('[data-modal-back]');
  const $closeBtn = dialog.querySelector('[data-modal-close]');
  const $progressBar = dialog.querySelector('[data-modal-progress-bar]');

  let config = null;        // { questions, rules, settings }
  let answers = {};         // { question_id: value }
  let history = [];         // ids of visited steps (for Back button)
  let currentId = null;     // current question_id, or '__review' / '__confirm'
  let lastTrigger = null;   // restore focus on close

  // ---------- Trigger interception ----------

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-modal-trigger]');
    if (!trigger) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    event.preventDefault();
    lastTrigger = trigger;
    open();
  });

  $closeBtn.addEventListener('click', () => close());
  dialog.addEventListener('cancel', (e) => { e.preventDefault(); close(); });
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close(); // backdrop click on the <dialog> element itself
  });
  $next.addEventListener('click', onNext);
  $back.addEventListener('click', onBack);

  // ---------- Open / close ----------

  function open() {
    if (typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('pre-contact-modal-open');

    if (!config) {
      loadConfig().then(() => {
        if (!currentId) startFlow();
      }).catch(showLoadError);
    } else if (!currentId) {
      startFlow();
    }
  }

  function close() {
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
    document.documentElement.classList.remove('pre-contact-modal-open');
    if (lastTrigger && typeof lastTrigger.focus === 'function') {
      lastTrigger.focus();
    }
  }

  function reset() {
    answers = {};
    history = [];
    currentId = null;
    $stepHost.innerHTML = '';
    $confirmHost.innerHTML = '';
    $confirmHost.hidden = true;
    $stepHost.hidden = false;
    $error.hidden = true;
    $error.textContent = '';
    setProgress(0);
  }

  // ---------- Config load ----------

  async function loadConfig() {
    showLoading(true);
    const url = endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + 'action=questions';
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) throw new Error('Config request failed: ' + res.status);
    const data = await res.json();
    if (data.error) throw new Error(data.error);
    config = data;
    showLoading(false);
    return config;
  }

  function showLoading(on) {
    $loading.hidden = !on;
    $stepHost.hidden = on;
  }

  function showLoadError(err) {
    console.error('[pre-contact-modal]', err);
    showLoading(false);
    $stepHost.hidden = true;
    $error.hidden = false;
    $error.innerHTML = "We couldn't load the form. <a href=\"" + (lastTrigger ? lastTrigger.dataset.fallbackMailto || lastTrigger.getAttribute('href') : 'mailto:sales@farmandacreco.com.au') + "\">Email us directly</a>.";
    $footer.hidden = true;
  }

  // ---------- Flow control ----------

  function startFlow() {
    reset();
    $footer.hidden = false;
    const next = nextVisibleQuestion(null);
    if (!next) {
      // No questions configured — go straight to review.
      goToReview();
      return;
    }
    currentId = next.question_id;
    renderStep(next);
  }

  function nextVisibleQuestion(afterId) {
    const all = orderedQuestions();
    const startIdx = afterId == null ? 0 : (all.findIndex(q => q.question_id === afterId) + 1);
    for (let i = startIdx; i < all.length; i++) {
      if (isQuestionVisible(all[i])) return all[i];
    }
    return null;
  }

  function orderedQuestions() {
    // Drop junk/blank sheet rows. A stray row (question_id "Q", no text, no input_type,
    // blank order) sorts to position 0 and renders as an empty unlabelled text box,
    // breaking the whole flow before the real first question. A real question always
    // has text or a usable input_type.
    return (config.questions || [])
      .filter(q => q && String(q.question_id || '').trim() !== '' &&
        (String(q.text || '').trim() !== '' || SUPPORTED_INPUT_TYPES.indexOf(String(q.input_type || '').trim()) !== -1))
      .slice()
      .sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
  }

  function isQuestionVisible(q) {
    if (!q.condition_expr) return true;
    return evalCondition(String(q.condition_expr), answers);
  }

  function onNext() {
    if (currentId === '__review') {
      submit();
      return;
    }
    const q = findQuestion(currentId);
    if (!q) return;
    const value = readStepValue(q);
    const validation = validateValue(q, value);
    if (!validation.ok) {
      flagError(validation.message);
      return;
    }
    if (q.input_type === 'contact') {
      answers.Q_NAME = value.name;
      answers.Q_EMAIL = value.email;
      answers.Q_PHONE = value.phone;
      answers[q.question_id] = '__contact_complete__';
    } else if (q.input_type === 'photo') {
      answers[q.question_id] = (value || []).join(', ');
    } else if (q.input_type !== 'info') {
      answers[q.question_id] = value;
    }
    history.push(currentId);

    const next = nextVisibleQuestion(q.question_id);
    if (next) {
      currentId = next.question_id;
      renderStep(next);
    } else {
      goToReview();
    }
  }

  function onBack() {
    if (!history.length) return;
    if (currentId === '__review') {
      currentId = history[history.length - 1];
      const q = findQuestion(currentId);
      if (q) renderStep(q);
      return;
    }
    currentId = history.pop();
    const q = findQuestion(currentId);
    if (q) renderStep(q);
  }

  function findQuestion(id) {
    return (config.questions || []).find(q => q.question_id === id) || null;
  }

  // ---------- Step rendering ----------

  function renderStep(q) {
    $stepHost.innerHTML = '';
    $stepHost.hidden = false;
    $confirmHost.hidden = true;
    $error.hidden = true;
    if ($loading) $loading.hidden = true;

    const stepIdx = history.length;
    const total = countLikelyVisible();
    setProgress(total ? Math.min(99, Math.round((stepIdx / total) * 100)) : 0);

    if ($title) $title.textContent = 'How can we help?';

    const wrap = el('div', 'pre-contact-modal__step');

    if (q.text && q.input_type !== 'info') {
      wrap.appendChild(el('h3', 'pre-contact-modal__question', q.text));
    }

    const inputType = SUPPORTED_INPUT_TYPES.indexOf(q.input_type) === -1 ? 'text' : q.input_type;
    wrap.appendChild(renderInput(q, inputType));

    if (q.helper_image_url) {
      const fig = el('figure', 'pre-contact-modal__helper-image');
      const img = document.createElement('img');
      img.src = q.helper_image_url;
      img.alt = '';
      img.loading = 'lazy';
      fig.appendChild(img);
      wrap.appendChild(fig);
    }

    if (q.helper_text || q.helper_link_url) {
      const helper = el('div', 'pre-contact-modal__helper');
      if (q.helper_text) helper.appendChild(el('p', 'pre-contact-modal__helper-text', q.helper_text));
      if (q.helper_link_url) {
        const a = document.createElement('a');
        a.href = q.helper_link_url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.className = 'pre-contact-modal__helper-link';
        a.textContent = q.helper_link_label || 'Learn more';
        helper.appendChild(a);
      }
      wrap.appendChild(helper);
    }

    $stepHost.appendChild(wrap);

    $back.hidden = history.length === 0;
    $next.textContent = q.input_type === 'info' ? 'Continue' : 'Continue';

    // Initial focus.
    const focusable = wrap.querySelector('input:not([type=hidden]), select, textarea, [data-radio-card]');
    if (focusable) {
      // Defer so dialog's own focus management doesn't fight us.
      setTimeout(() => focusable.focus(), 30);
    }
  }

  function renderInput(q, inputType) {
    if (inputType === 'radio') return renderRadioCards(q);
    if (inputType === 'select') return renderSelect(q);
    if (inputType === 'textarea') return renderTextarea(q);
    if (inputType === 'photo') return renderPhoto(q);
    if (inputType === 'contact') return renderContact(q);
    if (inputType === 'info') return renderInfo(q);
    // text / email / tel / number
    return renderTextInput(q, inputType);
  }

  function renderRadioCards(q) {
    const wrap = el('div', 'pre-contact-modal__radio-group');
    wrap.setAttribute('role', 'radiogroup');
    wrap.setAttribute('data-input-id', q.question_id);
    parseOptions(q.options_csv).forEach((opt, i) => {
      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'pre-contact-modal__radio-card';
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', 'false');
      card.dataset.value = opt;
      card.dataset.radioCard = 'true';
      card.tabIndex = 0;
      card.innerHTML = '<span class="pre-contact-modal__radio-card-label">' + escapeHtml(opt) + '</span>';
      card.addEventListener('click', () => {
        wrap.querySelectorAll('[data-radio-card]').forEach(c => c.setAttribute('aria-checked', 'false'));
        card.setAttribute('aria-checked', 'true');
        wrap.dataset.value = opt;
      });
      wrap.appendChild(card);
      if (i === 0 && answers[q.question_id]) {
        // Pre-select if user navigated back.
        if (answers[q.question_id]) wrap.dataset.value = answers[q.question_id];
      }
    });
    if (answers[q.question_id]) {
      const match = wrap.querySelector('[data-value="' + cssEscape(answers[q.question_id]) + '"]');
      if (match) match.setAttribute('aria-checked', 'true');
    }
    return wrap;
  }

  function renderSelect(q) {
    const wrap = el('div', 'pre-contact-modal__field');
    const sel = document.createElement('select');
    sel.className = 'pre-contact-modal__select';
    sel.dataset.inputId = q.question_id;
    sel.appendChild(new Option('Select…', ''));
    parseOptions(q.options_csv).forEach(opt => sel.appendChild(new Option(opt, opt)));
    if (answers[q.question_id]) sel.value = answers[q.question_id];
    wrap.appendChild(sel);
    return wrap;
  }

  function renderTextInput(q, type) {
    const wrap = el('div', 'pre-contact-modal__field');
    const input = document.createElement('input');
    input.type = type;
    input.className = 'pre-contact-modal__input';
    input.dataset.inputId = q.question_id;
    if (type === 'email') input.inputMode = 'email';
    if (type === 'tel') input.inputMode = 'tel';
    if (type === 'number') input.inputMode = 'numeric';
    input.value = answers[q.question_id] || '';
    wrap.appendChild(input);
    return wrap;
  }

  function renderTextarea(q) {
    const wrap = el('div', 'pre-contact-modal__field');
    const ta = document.createElement('textarea');
    ta.className = 'pre-contact-modal__textarea';
    ta.dataset.inputId = q.question_id;
    ta.rows = 5;
    ta.value = answers[q.question_id] || '';
    wrap.appendChild(ta);
    return wrap;
  }

  function renderInfo(q) {
    // Body already shows helper_text + helper_link via the shared block — render
    // an empty placeholder so the "Continue" button keeps the user in flow.
    const wrap = el('div', 'pre-contact-modal__info');
    wrap.dataset.inputId = q.question_id;
    return wrap;
  }

  function renderContact(q) {
    const wrap = el('div', 'pre-contact-modal__contact');
    wrap.dataset.inputId = q.question_id;

    wrap.appendChild(field('Name', 'text', 'Q_NAME', answers.Q_NAME || '', { required: true, autocomplete: 'name' }));
    wrap.appendChild(field('Email', 'email', 'Q_EMAIL', answers.Q_EMAIL || '', { required: true, autocomplete: 'email', inputmode: 'email' }));
    wrap.appendChild(field('Phone (optional)', 'tel', 'Q_PHONE', answers.Q_PHONE || '', { autocomplete: 'tel', inputmode: 'tel' }));
    return wrap;

    function field(label, type, id, value, opts) {
      opts = opts || {};
      const f = el('label', 'pre-contact-modal__field');
      f.appendChild(el('span', 'pre-contact-modal__field-label', label));
      const i = document.createElement('input');
      i.type = type;
      i.className = 'pre-contact-modal__input';
      i.dataset.contactField = id;
      i.value = value;
      if (opts.required) i.required = true;
      if (opts.autocomplete) i.autocomplete = opts.autocomplete;
      if (opts.inputmode) i.inputMode = opts.inputmode;
      f.appendChild(i);
      return f;
    }
  }

  function renderPhoto(q) {
    const wrap = el('div', 'pre-contact-modal__photo');
    wrap.dataset.inputId = q.question_id;

    const list = el('ul', 'pre-contact-modal__photo-list');
    const initial = (answers[q.question_id] || '').split(',').map(s => s.trim()).filter(Boolean);
    initial.forEach(u => list.appendChild(photoItem(u)));

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.className = 'pre-contact-modal__photo-input';
    input.id = 'pre-contact-photo-' + q.question_id;

    const trigger = document.createElement('label');
    trigger.htmlFor = input.id;
    trigger.className = 'farm-btn farm-btn--ghost pre-contact-modal__photo-trigger';
    trigger.textContent = 'Add photo';

    const status = el('p', 'pre-contact-modal__photo-status');
    status.hidden = true;

    input.addEventListener('change', async () => {
      const files = Array.from(input.files || []);
      input.value = '';
      if (!files.length) return;
      status.hidden = false;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        status.textContent = 'Uploading ' + (i + 1) + ' of ' + files.length + '…';
        try {
          const url = await uploadcareUpload(file);
          list.appendChild(photoItem(url));
        } catch (err) {
          console.error(err);
          status.textContent = "Couldn't upload " + file.name + ' — try a smaller image.';
          return;
        }
      }
      status.textContent = 'Done';
      setTimeout(() => { status.hidden = true; }, 2000);
    });

    wrap.appendChild(list);
    wrap.appendChild(trigger);
    wrap.appendChild(input);
    wrap.appendChild(status);
    return wrap;

    function photoItem(url) {
      const li = el('li', 'pre-contact-modal__photo-item');
      const img = document.createElement('img');
      img.src = url + '-/preview/200x200/';
      img.alt = '';
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'pre-contact-modal__photo-remove';
      remove.setAttribute('aria-label', 'Remove photo');
      remove.textContent = '×';
      remove.addEventListener('click', () => li.remove());
      li.appendChild(img);
      li.appendChild(remove);
      li.dataset.url = url;
      return li;
    }
  }

  // ---------- Read step value ----------

  function readStepValue(q) {
    if (q.input_type === 'radio') {
      const wrap = $stepHost.querySelector('[data-input-id="' + cssEscape(q.question_id) + '"]');
      return wrap ? (wrap.dataset.value || '') : '';
    }
    if (q.input_type === 'select') {
      const sel = $stepHost.querySelector('select[data-input-id="' + cssEscape(q.question_id) + '"]');
      return sel ? sel.value : '';
    }
    if (q.input_type === 'textarea') {
      const ta = $stepHost.querySelector('textarea[data-input-id="' + cssEscape(q.question_id) + '"]');
      return ta ? ta.value.trim() : '';
    }
    if (q.input_type === 'photo') {
      return Array.from($stepHost.querySelectorAll('.pre-contact-modal__photo-item')).map(li => li.dataset.url);
    }
    if (q.input_type === 'contact') {
      const wrap = $stepHost.querySelector('[data-input-id="' + cssEscape(q.question_id) + '"]');
      if (!wrap) return { name: '', email: '', phone: '' };
      return {
        name: (wrap.querySelector('[data-contact-field="Q_NAME"]') || {}).value?.trim() || '',
        email: (wrap.querySelector('[data-contact-field="Q_EMAIL"]') || {}).value?.trim() || '',
        phone: (wrap.querySelector('[data-contact-field="Q_PHONE"]') || {}).value?.trim() || ''
      };
    }
    if (q.input_type === 'info') {
      return '';
    }
    const input = $stepHost.querySelector('input[data-input-id="' + cssEscape(q.question_id) + '"]');
    return input ? input.value.trim() : '';
  }

  // ---------- Validation ----------

  function validateValue(q, value) {
    const rule = String(q.validation_rule || '').trim();

    if (q.input_type === 'contact') {
      if (!value.name) return { ok: false, message: 'Please enter your name.' };
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.email)) return { ok: false, message: 'Please enter a valid email.' };
      if (value.phone && !/^[\d\s+()\-]{6,}$/.test(value.phone)) return { ok: false, message: 'That phone number doesn\'t look right.' };
      return { ok: true };
    }

    if (q.input_type === 'info') return { ok: true };

    if (q.input_type === 'photo') {
      if (rule === 'required' && (!value || !value.length)) return { ok: false, message: 'Please add at least one photo.' };
      return { ok: true };
    }

    if (rule === 'required' && !String(value).length) {
      return { ok: false, message: 'This one\'s required.' };
    }
    if (rule === 'email' && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      return { ok: false, message: 'Please enter a valid email.' };
    }
    if (rule === 'phone' && value && !/^[\d\s+()\-]{6,}$/.test(value)) {
      return { ok: false, message: 'That phone number doesn\'t look right.' };
    }
    if (rule.indexOf('regex:') === 0) {
      try {
        const pattern = new RegExp(rule.slice(6));
        if (value && !pattern.test(value)) return { ok: false, message: 'Doesn\'t look right — please check.' };
      } catch (e) { /* ignore bad regex from sheet */ }
    }
    return { ok: true };
  }

  function flagError(message) {
    $error.hidden = false;
    $error.textContent = message;
    setTimeout(() => { $error.hidden = true; }, 4500);
  }

  // ---------- Review + submit ----------

  async function goToReview() {
    currentId = '__review';
    setProgress(95);
    $stepHost.innerHTML = '';
    $stepHost.hidden = false;
    $confirmHost.hidden = true;
    if ($loading) $loading.hidden = true;

    const wrap = el('div', 'pre-contact-modal__review');
    if ($title) $title.textContent = 'Review and send';

    // Smart resolution lookup
    let smart = null;
    try {
      const url = endpoint + (endpoint.indexOf('?') === -1 ? '?' : '&') + 'action=smartResolve&answers=' + encodeURIComponent(JSON.stringify(answers));
      const res = await fetch(url);
      const data = await res.json();
      smart = (data && data.match) || null;
    } catch (err) {
      console.warn('[pre-contact-modal] smart resolve failed', err);
    }

    if (smart) {
      const card = el('div', 'pre-contact-modal__smart');
      card.appendChild(el('p', 'pre-contact-modal__smart-eyebrow', 'Based on your answers'));
      if (smart.title) card.appendChild(el('h3', 'pre-contact-modal__smart-title', smart.title));
      if (smart.body) card.appendChild(el('p', 'pre-contact-modal__smart-body', smart.body));
      if (smart.product_url) {
        const a = document.createElement('a');
        a.href = smart.product_url;
        a.target = '_blank';
        a.rel = 'noopener';
        a.className = 'farm-btn farm-btn--primary pre-contact-modal__smart-cta';
        a.textContent = smart.cta_label || 'View recommendation';
        a.addEventListener('click', () => { answers.__smart_clicked = '1'; });
        card.appendChild(a);
      }
      wrap.appendChild(card);
      wrap.dataset.smartRule = smart.rule_id || '';
    }

    // Summary
    const summary = el('dl', 'pre-contact-modal__summary');
    orderedQuestions().forEach(q => {
      if (!isQuestionVisible(q)) return;
      if (q.input_type === 'info') return;
      let display;
      if (q.input_type === 'contact') {
        display = (answers.Q_NAME || '') + (answers.Q_EMAIL ? ' · ' + answers.Q_EMAIL : '') + (answers.Q_PHONE ? ' · ' + answers.Q_PHONE : '');
      } else if (q.input_type === 'photo') {
        const urls = (answers[q.question_id] || '').split(',').map(s => s.trim()).filter(Boolean);
        display = urls.length ? urls.length + ' photo' + (urls.length > 1 ? 's' : '') + ' attached' : '—';
      } else {
        display = answers[q.question_id] || '—';
      }
      summary.appendChild(el('dt', 'pre-contact-modal__summary-q', q.text));
      summary.appendChild(el('dd', 'pre-contact-modal__summary-a', display));
    });
    wrap.appendChild(summary);

    $stepHost.appendChild(wrap);
    $next.textContent = 'Send to Farm & Acre Co';
    $back.hidden = false;
  }

  async function submit() {
    $next.disabled = true;
    $back.disabled = true;
    $next.textContent = 'Sending…';
    $error.hidden = true;

    const reviewWrap = $stepHost.querySelector('.pre-contact-modal__review');
    const smartShown = reviewWrap ? reviewWrap.dataset.smartRule : '';

    const photoUrls = [];
    Object.keys(answers).forEach(k => {
      const q = findQuestion(k);
      if (q && q.input_type === 'photo') {
        (answers[k] || '').split(',').map(s => s.trim()).filter(Boolean).forEach(u => photoUrls.push(u));
      }
    });

    const topic = answers.Q_TOPIC || '';

    const payload = {
      action: 'submitLead',
      name: answers.Q_NAME || '',
      email: answers.Q_EMAIL || '',
      phone: answers.Q_PHONE || '',
      topic: topic,
      answers: answers,
      smartResolutionShown: smartShown,
      smartResolutionClicked: !!answers.__smart_clicked,
      photoUrls: photoUrls,
      userAgent: navigator.userAgent,
      referrer: document.referrer || location.href
    };

    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        // text/plain dodges CORS preflight; Apps Script reads e.postData.contents either way.
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      showConfirmation();
    } catch (err) {
      console.error(err);
      $next.disabled = false;
      $back.disabled = false;
      $next.textContent = 'Send to Farm & Acre Co';
      flagError("Couldn't send right now. Please try again or email us directly.");
    }
  }

  function showConfirmation() {
    currentId = '__confirm';
    setProgress(100);
    $stepHost.hidden = true;
    $confirmHost.hidden = false;
    $footer.hidden = true;
    if ($title) $title.textContent = 'Thanks';

    const settings = config.settings || {};
    $confirmHost.innerHTML = '';
    const wrap = el('div', 'pre-contact-modal__confirmation-inner');
    wrap.appendChild(el('div', 'pre-contact-modal__confirmation-tick', '✓'));
    wrap.appendChild(el('h3', 'pre-contact-modal__confirmation-title', settings.confirmation_heading || 'Thanks — we got it.'));
    wrap.appendChild(el('p', 'pre-contact-modal__confirmation-body', settings.confirmation_body || "We'll get back to you " + (settings.response_sla_text || 'within 1 business day') + '.'));
    if (settings.faq_link) {
      const a = document.createElement('a');
      a.href = settings.faq_link;
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = 'farm-btn farm-btn--ghost';
      a.textContent = 'Browse our FAQs';
      wrap.appendChild(a);
    }
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'farm-btn farm-btn--primary';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', close);
    wrap.appendChild(closeBtn);

    $confirmHost.appendChild(wrap);
  }

  // ---------- Helpers ----------

  function setProgress(pct) {
    if ($progressBar) $progressBar.style.width = pct + '%';
  }

  function countLikelyVisible() {
    return orderedQuestions().filter(isQuestionVisible).length || 1;
  }

  function parseOptions(csv) {
    return String(csv || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  function cssEscape(s) {
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  // Same whitelist + sandbox model as the Apps Script side, mirrored client-side
  // so we can hide/show questions without round-tripping. Keep the two in sync.
  function evalCondition(expr, ans) {
    const sanitized = String(expr).trim();
    if (!sanitized) return true;
    const allowed = /^([\s()!&|=<>]|==|!=|&&|\|\||true|false|null|Q_[A-Z0-9_]+|'[^']*'|"[^"]*"|-?\d+(?:\.\d+)?)+$/;
    if (!allowed.test(sanitized.replace(/\s+/g, ' '))) {
      console.warn('[pre-contact-modal] rejected condition:', sanitized);
      return false;
    }
    const idents = (sanitized.match(/Q_[A-Z0-9_]+/g) || []).filter((v, i, a) => a.indexOf(v) === i);
    try {
      const fn = new Function(idents.join(','), 'return (' + sanitized + ');');
      return Boolean(fn.apply(null, idents.map(id => ans[id] != null ? String(ans[id]) : '')));
    } catch (err) {
      console.warn('[pre-contact-modal] eval error', err, sanitized);
      return false;
    }
  }

  // Direct-fetch Uploadcare upload — same pattern as assets/file-upload-complete.js
  async function uploadcareUpload(file) {
    const fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', UPLOADCARE_PUBLIC_KEY);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', file);
    const res = await fetch(UPLOADCARE_ENDPOINT, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('Uploadcare upload failed: ' + res.status);
    const data = await res.json();
    if (!data || !data.file) throw new Error('Uploadcare returned no file id');
    return UPLOADCARE_CDN + data.file + '/';
  }
})();
